const _origFetch = window.fetch;
window.fetch = function(...args) {
  const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
  const method = args[1]?.method || 'GET';
  if (url?.includes('leads')) {
    console.log(`🌐 fetch intercepted: ${method} ${url}`);
    console.trace();
  }
  return _origFetch.apply(this, args);
};

import { useState, useEffect, useCallback, useRef } from 'react';
import React from 'react';
import SolarCommandCenter from './solar-command-center';
import SolarLandingPage from './SolarLandingPage';

/* React Router imports */
import {BrowserRouter, Routes, Route} from 'react-router-dom';

/* authentication token helpers */
const TOKEN_KEY = 'solariq_token';
const ROLE_KEY = 'solariq_role';

function saveAuth(token, role) {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(ROLE_KEY, role);
}

function clearAuth() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(ROLE_KEY);
}

function getToken() { return sessionStorage.getItem(TOKEN_KEY); }
function getRole() { return sessionStorage.getItem(ROLE_KEY); }
function isAuthed() { return !!getToken(); }

// attach created token to every API call
function authHeaders() {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
}

// Error boundary — catches render crashes and shows a message instead of white screen
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('SolarIQ render error:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0a0e2e 0%, #011171 60%, #0a1a5c 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, fontFamily: 'sans-serif', color: '#fff', padding: 24 }}>
          <div style={{ fontSize: 48 }}>☀️</div>
          <div style={{ fontWeight: 800, fontSize: 20 }}>Something went wrong</div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, maxWidth: 500, textAlign: 'center' }}>
            {this.state.error?.message || 'An unexpected error occurred.'}
          </div>
          {this.state.error?.stack && (
            <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, maxWidth: 600, textAlign: 'left', background: 'rgba(0,0,0,0.3)', borderRadius: 8, padding: '10px 14px', marginTop: 8, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {this.state.error.stack.slice(0, 400)}
            </div>
          )}
          <button onClick={() => window.location.reload()} style={{ marginTop: 8, background: 'linear-gradient(135deg,#f5a623,#ff6b00)', border: 'none', borderRadius: 10, color: '#fff', padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            🔄 Reload App
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Server-side lead API — same leads on every device ──
async function apiSaveLead(lead, isUpdate = false) {
  try {
    console.log("📡 Sending to leads.js — billsArray count:", lead.billsArray?.length);

    const method = isUpdate ? 'PATCH' : 'POST';
    const body = isUpdate ? JSON.stringify({ id: lead.id, updates: lead }) : JSON.stringify(lead);
    const res = await fetch('/leads', { method, headers: authHeaders(), body });
    const data = await res.json();
    console.log("📡 leads.js response:", data);
    return {ok: res.ok, data};

  } catch (e) { 
    console.error('Save lead failed:', e);
    return {ok: false};  
  }
}

async function apiSaveSetting(key, value) {
  try {
    await fetch('/settings', { method: 'POST', headers: authHeaders(), body: JSON.stringify({ key, value }) });
  } catch (e) { console.error('Save setting failed:', e); }
}

async function apiLoadSetting(key) {
  try {
    const res = await fetch(`/settings?key=${encodeURIComponent(key)}`, { headers: authHeaders() });
    if (!res.ok) return null;
    const data = await res.json();
    return data.value ?? null;
  } catch (e) { console.error('Load setting failed:', e); return null; }
}

async function apiDeleteSetting(key) {
  try {
    await fetch('/settings', { method: 'DELETE', headers: authHeaders() , body: JSON.stringify({ key }) });
  } catch (e) { console.error('Delete setting failed:', e); }
}

async function apiDeleteLead(id) {
  try {
    const res = await fetch(`/leads?id=${id}`, 
      { method: 'DELETE', headers: authHeaders(), });
    if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
  } catch (e) { 
    console.error('Delete lead failed:', e); 
    throw e;
  }
}

async function apiClearLeads() {
  try {
    await fetch('/leads', { method: 'DELETE', headers: authHeaders(), body: JSON.stringify({ clearAll: true }) });
  } catch (e) { console.error('Clear leads failed:', e); }
}

async function apiLoadLeads() {
  console.trace("📡 apiLoadLeads called");
  try {
    const res = await fetch('/leads', {headers: authHeaders()});
    if (!res.ok) return [];
    return await res.json();
  } catch (e) { console.error('Load leads failed:', e); return []; }
}

// TEMPORARY LEAD SCORING - applicable if no qualifier questions answered.
/* function scoreLead(payload, bill, adviserOnly) {
  let score = 0;

  if (payload.ownsHome === true) score += 40;
  else if (payload.ownsHome === false) score += 0;
  else score += 15;

  if      (bill >= 5000) score += 35;
  else if (bill >= 3000) score += 28;
  else if (bill >= 2000) score += 22;
  else if (bill >= 1500) score += 16;
  else if (bill >= 1000) score += 10;
  else if (bill >= 500)  score += 5;
  else                   score += 0;

  const tl = (payload.installTimeline || '').toLowerCase();
  if      (tl.includes('asap') || tl.includes('1 month'))  score += 25;
  else if (tl.includes('1 –') || tl.includes('1-3'))       score += 18;
  else if (tl.includes('3 –') || tl.includes('3-6'))       score += 10;
  else if (tl.includes('exploring'))                       score += 2;
  else                                                     score += 8;

  score = Math.min(score, 100);
  const stage = score >= 75 ? 'HOT' : score >= 50 ? 'WARM' : 'COLD';
  const intent = score >= 75 ? 'Very High' : score >= 50 ? 'High' : 'Medium';
  return { score, stage, intent, closeProbability: score };
} */

function buildLead(payload) {
  const adviserOnly = payload.adviserOnly === true;
  const bill = payload.bill != null ? payload.bill : (adviserOnly ? 0 : 1500);
  const sysKw = payload.systemKw != null ? payload.systemKw : (adviserOnly ? 0 : parseFloat((bill / 14).toFixed(1)));
  const systemCost = payload.systemCost != null ? payload.systemCost : (sysKw > 0 ? Math.round(sysKw * 18000) : 0);
  const taxCredit = payload.taxCredit != null ? payload.taxCredit : Math.round(systemCost * 0.25);
  const netCost = payload.netCost != null ? payload.netCost : systemCost - taxCredit;
  

  //const { score: intakeScore } = scoreLead(payload, bill);
  const score = payload.skipScore ? null : (payload.qualifierScore || null);
  const stage = score === null ? "COLD" : score >= 75 ? "HOT" : score >= 50 ? "WARM" : "COLD";
  const intent = score === null ? 'Medium' : score >= 75 ? "Very High" : score >= 50 ? "High" : "Medium";

  return {
    id: payload.id || `landing-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
    name: payload.name || 'Unknown', phone: payload.phone || '', email: payload.email || '',
    address: '', city: `${payload.city || ''}${payload.province ? ', ' + payload.province : ''}`,
    propertyAddress: payload.propertyAddress || payload.billAddress || '',
    province: payload.province || '', homeYear: 2015, sqft: 1500, roofAge: 8, roofType: 'Unknown',
    utility: 'Eskom', monthlyBill: bill, systemSize: sysKw, systemCost, taxCredit,
    netCost,
    monthlyPayment: payload.monthlyPayment != null ? payload.monthlyPayment : (sysKw > 0 ? Math.round((systemCost - taxCredit) / 60) : 0),
    monthlySavings: payload.monthlySavings != null ? payload.monthlySavings : (bill > 0 ? Math.round(bill * 0.82) : 0),
    annualSavings: payload.annualSavings != null ? payload.annualSavings : ((payload.monthlySavings != null ? payload.monthlySavings : (bill > 0 ? Math.round(bill * 0.82) : 0)) * 12),
    provCr: payload.provCr != null ? payload.provCr : 0,
    paybackYears: payload.paybackYears != null ? payload.paybackYears :  0,
    lifetime25yr: payload.lifetime25yr != null ? payload.lifetime25yr :  0,
    sec12bBenefit: payload.sec12bBenefit || payload.sec12b || 0,
    coverageRatio: payload.coverageRatio != null ? payload.coverageRatio : 0,
    sizedFrom: payload.sizedFrom || 'bill_estimate',
    monthlyKwh: payload.monthlyKwh != null ? payload.monthlyKwh : 0,
    tariffRate: payload.tariffRate != null ? payload.tariffRate : 0,
    
    score, stage, intent, closeProbability: score ?? 0,
    source: 'Meta Landing Page', sourceKey: 'meta',
    adSet: adviserOnly ? 'Meta — Callback Request' : 'Meta Lead Form', adSpend: null,
    status: payload.status || 'New', notes: payload.notes || '',
    callTime: payload.callTime || '', installTimeline: payload.installTimeline || '',
    ownsHome: payload.ownsHome, saved: false, contacted: false,
    answers: {}, createdAt: new Date().toISOString(), callScheduled: null,
    adviserOnly, partial: payload.partial || false,
    billImageB64: payload.billImageB64 || null, billImageMime: payload.billImageMime || null,
    billFileName: payload.billFileName || null, billUploadMethod: payload.billUploadMethod || 'none',
    billsArray: payload.billsArray || [],
    qualifyAnswers: payload.qualifyAnswers || {},
    qualifyType: payload.qualifyType || "residential",
    hasBill: (payload.billsArray?.length > 0) || !!payload.billImageB64,
  };
}

function AppInner({startView}) {
  const view = startView || 'landing';

  const [authed, setAuthed] = useState(isAuthed);
  const [password, setPassword] = useState('');
  const [loginRole, setLoginRole] = useState('admin');
  const [error, setError] = useState('');
  const [storedLeads, setStoredLeads] = useState([]);
  const [storedMetaAccounts, setStoredMetaAccounts] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  
  const sessionLeads = useRef([]);

  useEffect(() => {
    const savedId = sessionStorage.getItem("ssn_partialLeadId");
    console.log("🌱 seeding sessionLeads with:", savedId);
    if (savedId && !sessionLeads.current.find(l => l.id === savedId)) {
      sessionLeads.current = [{ id: savedId }];
    }
  }, []);
  
  const pendingDeletes = useRef(new Set());
  const deletedLeads = useRef(new Set());

  const refreshLeads = useCallback(async () => {
    const leads = await apiLoadLeads();
    setStoredLeads(leads);
  }, []);

  useEffect(() => {
    if (view === 'admin' && authed) {
      setLeadsLoading(true);
      // Load leads AND meta accounts in parallel — only show app once BOTH are ready
      Promise.all([
        apiLoadLeads(),
        apiLoadSetting('meta_accounts'),
      ]).then(([leads, metaAccounts]) => {
        setStoredLeads((leads || []).filter(l => !pendingDeletes.current.has(l.id
          
        )));
        setStoredMetaAccounts(metaAccounts || []);
      }).catch(e => {
        console.error("❌ Admin load failed:", e);
        setStoredLeads([]);
        setStoredMetaAccounts([]);
      }).finally(() => setLeadsLoading(false));

      // Poll for new leads every 15s
      const interval = setInterval(async () => {
        const fresh = await apiLoadLeads();
        /*setStoredLeads(prev => {
          if (fresh.length !== prev.length) return fresh;
          const freshIds = fresh.map(l => l.id).join(',');
          const prevIds = prev.map(l => l.id).join(',');
          return freshIds !== prevIds ? fresh : prev;
        }); */
        setStoredLeads(fresh.filter(l => !pendingDeletes.current.has(l.id)));
      }, 15000);
      return () => clearInterval(interval);
    }
  }, [view, authed]);

  async function handleLogin() {
    /* if (password === ADMIN_PASSWORD) { setError(''); 
      setIsAuth(true); 
      localStorage.setItem("solariq_admin_auth", "true"); 
      window.location.href = "/admin/panel"; }
    else { setError('Incorrect password. Try again.'); setPassword(''); } */

    setError('');
    try {
      const res = await fetch('/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json'},
        body: JSON.stringify({ role: loginRole, password}),
      });
      const data = await res.json();
      if (!res.ok || !data.token) {
        setError('Incorrect password. Try again.');
        setPassword('');
        return;
      }
      saveAuth(data.token, data.role);
      setAuthed(true);
      setPassword('');
      window.location.href = '/admin/panel';
    }
    catch (e) {
      setError('Login failed. Please try again.');
      setPassword('');
    }
  }

  function handleLogout() {
    clearAuth();
    setAuthed(false);
    window.location.href = '/';
  }

  if (view === 'login') {
    return (
      <div style={{ minHeight: '100vh', margin: 0, padding: 0, background: 'linear-gradient(135deg, #0a0e2e 0%, #011171 60%, #0a1a5c 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Syne', sans-serif" }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;700;800&display=swap'); html, body { margin: 0; padding: 0; background: #0a0e2e; }`}</style>
        <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 20, padding: '40px 36px', width: '100%', maxWidth: 380, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>☀️</div>
          <div style={{ fontWeight: 800, fontSize: 22, color: '#ffffff', marginBottom: 6 }}>SolarIQ Admin</div>
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, marginBottom: 28 }}>Enter your password to access the Command Center</div>
          <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLogin()} autoFocus
            style={{ width: '100%', background: 'rgba(255,255,255,0.12)', border: `1.5px solid ${error ? '#ef4444' : 'rgba(255,255,255,0.25)'}`, borderRadius: 10, color: '#ffffff', padding: '12px 14px', fontSize: 15, fontFamily: 'inherit', marginBottom: 10, boxSizing: 'border-box', outline: 'none' }} />
          {error && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 12 }}>{error}</div>}
          <button onClick={handleLogin} style={{ width: '100%', background: 'linear-gradient(135deg,#f5a623,#ff6b00)', border: 'none', borderRadius: 10, color: '#fff', padding: '13px', fontSize: 15, fontWeight: 800, cursor: 'pointer', marginBottom: 16 }}>🔐 Enter Command Center</button>
          <button onClick={() => window.location.href = "/"} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>← Back to landing page</button>
        </div>
      </div>
    );
  }

  if (view === 'admin') {
    if (!authed) {
      window.location.href = "/admin";
      return null;
    }
    
    return (
      <div style={{ position: 'relative' }}>
        <button onClick= {handleLogout}
          style={{ position: 'fixed', top: 8, left: 8, zIndex: 9999, background: 'rgba(3,20,119,0.56)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', padding: '3px 8px', borderRadius: 6, fontSize: 13, fontWeight: 900, cursor: 'pointer', fontFamily: 'inherit' }} >
          ←
        </button>
        {leadsLoading ? (
          <div style={{ minHeight: '100vh', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, color: '#555', fontFamily: "'Syne', sans-serif" }}>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ fontSize: 48, animation: 'spin 2s linear infinite', display: 'inline-block' }}>☀️</div>
            <div style={{ fontSize: 14 }}>Loading leads from server…</div>
          </div>
        ) : (
          <SolarCommandCenter
            initialLeads={storedLeads}
            initialMetaAccounts={storedMetaAccounts}

            adminRole={getRole()}

            onSaveLead={async (lead) => {
              if (deletedLeads.current.has(lead.id)) return;
              console.log("💾 onSaveLead called for:", lead.id, lead.name);
              const exists = storedLeads.find(l => l.id === lead.id);
              console.log("💾 exists in storedLeads:", !!exists);
              await apiSaveLead(lead, !!exists);
              setStoredLeads(prev => {
                const exists = prev.find(l => l.id === lead.id);
                return exists 
                ? prev.map(l => l.id === lead.id ? lead : l)
                : [lead, ...prev];
              });
            }}

            onSaveMetaAccounts={async (accounts) => {
              // Strip sensitive tokens before saving? No — we need them to reconnect.
              // They're protected by the admin password.
              await apiSaveSetting('meta_accounts', accounts);
            }}
            onDeleteMetaAccounts={async () => {
              await apiDeleteSetting('meta_accounts');
            }}
            onDeleteLead={async (id) => {
              pendingDeletes.current.add(id);
              deletedLeads.current.add(id);
              setStoredLeads(prev => prev.filter(l => l.id !== id));
              try {
                await apiDeleteLead(id);
              } finally {
                pendingDeletes.current.delete(id);
              }
            }}

            onClearLeads={async () => {
              await apiClearLeads();
              setStoredLeads([]);
            }}
            onUpdateLead={async (id, updates) => {
              if (deletedLeads.current.has(id)) return;
              try {
                await fetch('/leads', { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ id, updates }) });
              } catch (e) { console.error(e); }
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <SolarLandingPage
        onLeadCaptured={async (payload) => {
          console.log("🔔 onLeadCaptured fired", payload.id, 
            "pendingDeletes:", [...pendingDeletes.current], 
            "deletedLeads:", [...deletedLeads.current]
          );

          // guard against async calls on already deleted leads (i.e. redisplaying or PATCHing an already deletedLead)
          if (pendingDeletes.current.has(payload.id) || deletedLeads.current.has(payload.id)) {
            console.log("onLeadCaptured blocked - lead pending delete:", payload.id);
            return;
          }
          console.log("🔔 onLeadCaptured fired", payload.id, "sessionLeads:", sessionLeads.current.map(l => l.id));

          const isUpdate = !!sessionLeads.current.find(l => l.id === payload.id);
          console.log("🔔 isUpdate:", isUpdate, "payload.id:", payload.id, "sessionLeads:", sessionLeads.current.map(l => l.id));
          
          if (isUpdate) {
            const res = await fetch('/leads', {
              method: "PATCH",
              headers: authHeaders(),
              body: JSON.stringify({ id: payload.id, updates: payload})
            });
            const data = await res.json();
            console.log("📡 PATCH response:", res.status, data);
            console.log(`PATCH option: patched lead is ${payload.id}`); 
            sessionLeads.current = sessionLeads.current.map(l => l.id === payload.id ? { ...l, ...payload } : l);
          }

          else {
            // POST - new lead
            console.log(`POST: current partialLead is ${payload.id}`);
            console.log("buildLead billsArray in:", payload.billsArray?.length, payload.billsArray?.map(b => ({ name: b.name, b64Len: b.b64?.length })));
            const lead = buildLead(payload);

            sessionLeads.current = [lead, ...sessionLeads.current];

            console.log("buildLead billsArray out:", lead.billsArray?.length);
            const result = await apiSaveLead(lead, false);
            return result;
          }
        }}
      />
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
           <Route path="/" element={<AppInner />} />
           <Route path="/admin" element={<AppInner startView="login" />} />
           <Route path="/admin/panel" element={<AppInner startView="admin" />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
